import React from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid2 as Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import BillingIntervalInput from './BillingIntervalInput';
import DueAfterInput from './DueAfterInput';
import PlanTypeInput from './PlanTypeInput';
import PricingModelInput from './PricingModelInput';
import BillingSchemeInput from './BillingSchemeInput';
import TieredPriceInput from './TieredPriceInput';
import appConfig from '../../config/appConfig';

const PlanFormLayout = ({ formData, updateFormData, disableFields = false, isEdit = false }) => {
  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Name"
            placeholder={'Enter Plan Name'}
            value={formData.name}
            onChange={(e) => updateFormData({ ...formData, name: e.target.value })}
            size="small"
            disabled={disableFields || isEdit}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Description"
            placeholder={'Enter Plan Description'}
            value={formData.description}
            onChange={(e) => updateFormData({ ...formData, description: e.target.value })}
            size="small"
            disabled={disableFields || isEdit}
            required
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 1.5 }}>
          <TextField
            type="text"
            label="Display Order"
            placeholder={'Enter Display Order'}
            value={
              formData.position === '' ? '' : new Intl.NumberFormat(window.navigator.language).format(formData.position)
            }
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9.]/g, '');
              updateFormData({
                ...formData,
                position: value === '' ? '' : parseFloat(value),
              });
            }}
            size="small"
            required
            disabled={disableFields || isEdit}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 1.5 }}>
          <FormControl variant="standard" fullWidth>
            <FormLabel>
              Self Serve{' '}
              <Switch
                onChange={(e) => updateFormData({ ...formData, self_serve: e.target.checked })}
                disabled={disableFields || isEdit}
              />
            </FormLabel>
          </FormControl>
        </Grid>
      </Grid>
      <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
        <Typography variant="h6" color="text.secondary">
          Billing Period
        </Typography>
      </Divider>
      <Grid container spacing={2}>
        <BillingIntervalInput formData={formData} onChange={updateFormData} disabled={disableFields || isEdit} />
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
          <DueAfterInput formData={formData} onChange={updateFormData} disabled={disableFields || isEdit} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
          <PlanTypeInput
            onChange={updateFormData}
            formData={formData}
            disabled={disableFields || isEdit}
          ></PlanTypeInput>
        </Grid>
        {formData.type === 'postpaid' ? (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
            <PricingModelInput
              formData={formData}
              onChange={updateFormData}
              planType={formData.type}
              disabled={disableFields || isEdit}
            />
          </Grid>
        ) : null}
        {formData.pricing_model === 'metered' ? (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
            <BillingSchemeInput formData={formData} onChange={updateFormData} disabled={disableFields || isEdit} />
          </Grid>
        ) : null}

        {formData.billing_schema === 'tiered' ? (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel shrink>Mode</InputLabel>
              <Select
                label="Mode"
                value={formData.tiers_mode}
                onChange={(e) => updateFormData({ ...formData, tiers_mode: e.target.value })}
                required
                placeholder="Enter your tiers mode"
                size="small"
                disabled={disableFields || isEdit}
              >
                <MenuItem value="volume">Volume</MenuItem>
                <MenuItem value="graduated">Graduated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        ) : (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
            <TextField
              label={formData.billing_schema === 'per_unit' ? 'Per Document' : 'Price'}
              type="number"
              value={formData.price}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                updateFormData({
                  ...formData,
                  price: Number(value),
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                  e.preventDefault();
                }
              }}
              required
              fullWidth
              placeholder="Enter Price"
              size="small"
              disabled={disableFields || isEdit}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">{appConfig.stripeCurrency}</InputAdornment>,
                },
              }}
            />
          </Grid>
        )}
      </Grid>

      {formData.billing_schema === 'tiered' ? (
        <>
          <TieredPriceInput formData={formData} onChange={updateFormData} disabled={disableFields || isEdit} />
        </>
      ) : null}

      <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
        <Typography variant="h6" color="text.secondary">
          Usage Limits
        </Typography>
      </Divider>
      <Grid container spacing={2}>
        <Grid size={2}>
          <TextField
            fullWidth
            label="Documents Limit"
            type="text"
            placeholder={formData.pricing_model === 'metered' ? '∞' : 'Enter Documents Limit'}
            value={
              formData.documents_limit
                ? new Intl.NumberFormat(window.navigator.language).format(formData.documents_limit)
                : ''
            }
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9.]/g, '');
              updateFormData({
                ...formData,
                documents_limit: value === '' ? '' : parseFloat(value),
              });
            }}
            size="small"
            disabled={formData.pricing_model === 'metered'}
            required
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PlanFormLayout;
